import * as React from 'react'
import { UserX } from 'lucide-react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { LOST_REASONS, LOST_REASON_LABELS, type LostReason } from '@lib/enums/lost-reason.enum'
import { loseEventInputSchema } from '@lib/input/event.input'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { useLoseEvent, useReopenEvent } from '@/hooks/useEvents'
import { requestErrorMessage } from '@/helpers/apiClient'

/** The longest note a loss takes, as the API's input allows. */
const NOTE_MAX = 2000
/** The note's counter shows from here on, so it is there before it matters. */
const NOTE_COUNTER_FROM = 1800

/**
 * The latest move to `lost` in an event's history: why it was lost this time.
 * An inquiry lost, reopened and lost again has two, and the banner names the
 * last.
 */
function latestLoss(event: EventDetailDto): { reason: LostReason | null; note: string | null } | null {
  const losses = (event.stateChanges ?? []).filter((c) => c.to === 'lost')
  const last = losses[losses.length - 1]
  return last === undefined ? null : { reason: last.lostReason, note: last.note }
}

interface LoseInquiryDialogProps {
  event: EventDetailDto
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Mark an inquiry lost: a reason from the list every company shares, and an
 * optional note. See `docs/funnel-stages.spec.md`.
 */
export function LoseInquiryDialog({ event, open, onOpenChange }: LoseInquiryDialogProps) {
  const lose = useLoseEvent(event.id)
  const { toast } = useToast()
  const [reason, setReason] = React.useState<LostReason | null>(null)
  const [note, setNote] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  function reset(): void {
    setReason(null)
    setNote('')
    setError(null)
  }

  /** Closed by any way out, it starts empty next time: no reason left picked that wasn't meant. */
  function close(): void {
    onOpenChange(false)
    reset()
  }

  function submit(): void {
    if (reason === null) return
    const parsed = loseEventInputSchema.safeParse({ reason, ...(note.trim() === '' ? {} : { note }) })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the note and try again.')
      return
    }
    setError(null)
    lose.mutate(parsed.data, {
      onSuccess: () => {
        close()
        toast('Marked lost')
      },
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true)
        else close()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark this inquiry lost?</DialogTitle>
          <DialogDescription>
            “{event.name}” stays on record and counts in the venue’s conversion rates. You can reopen it if the couple
            comes back.
          </DialogDescription>
        </DialogHeader>
        <ChoiceGroup legend="Why was it lost?" columns={2}>
          {LOST_REASONS.map((r) => (
            <ChoiceCard
              key={r}
              type="radio"
              size="compact"
              name="lost-reason"
              checked={reason === r}
              onSelect={() => setReason(r)}
              title={LOST_REASON_LABELS[r]}
            />
          ))}
        </ChoiceGroup>
        <label className="grid gap-1.5 text-sm">
          <span className="font-medium">
            Note <span className="font-normal text-ink-muted">(optional)</span>
          </span>
          <Textarea value={note} maxLength={NOTE_MAX} onChange={(e) => setNote(e.target.value)} />
          {note.length > NOTE_COUNTER_FROM ? (
            <span className="text-xs text-ink-muted">
              {note.length} of {NOTE_MAX}
            </span>
          ) : null}
        </label>
        {error ? <FormError>{error}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button pending={lose.isPending} type="button" disabled={reason === null} onClick={submit}>
            Mark lost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface LostNoticeProps {
  event: EventDetailDto
  canEdit: boolean
}

/**
 * What a lost inquiry says at the top of its page: that it was lost and why,
 * with the way back for those who can edit it.
 */
export function LostNotice({ event, canEdit }: LostNoticeProps) {
  const reopen = useReopenEvent(event.id)
  const { toast } = useToast()
  const [confirming, setConfirming] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const loss = latestLoss(event)
  const reasonLabel = loss?.reason ? LOST_REASON_LABELS[loss.reason] : 'No reason recorded'

  return (
    <>
      <Callout
        icon={UserX}
        title={`Lost: ${reasonLabel}`}
        action={
          canEdit ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setError(null)
                setConfirming(true)
              }}
            >
              Reopen
            </Button>
          ) : undefined
        }
      >
        {loss?.note ? <p className="m-0 whitespace-pre-line">{loss.note}</p> : null}
      </Callout>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Reopen this inquiry?"
        description="It goes back to being an inquiry and keeps everything it had."
        confirmLabel="Reopen"
        busy={reopen.isPending}
        error={error}
        onConfirm={() => {
          setError(null)
          reopen.mutate(
            {},
            {
              onSuccess: () => {
                setConfirming(false)
                toast('Reopened')
              },
              onError: (e) => setError(requestErrorMessage(e)),
            },
          )
        }}
      />
    </>
  )
}
